import type { AutomationRecipe } from '@/lib/automations';
import { useAutomationRecipes } from '@/hooks/use-automations';
import { useExtensionsStore } from '@/stores/extensions';
import { Button } from '@/components/ui/button';
import {
  Zap,
  ArrowRight,
  User,
  Tag,
  Bell,
  MessageSquare,
  Calendar,
  LayoutGrid,
  Bot,
  Webhook,
  ListPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AutomationRecipe as Recipe } from '@/lib/automations';

// Map the primary action type to an icon. Falls back to Zap.
function recipeIcon(r: Recipe): LucideIcon {
  const actionTypes = r.actions.map((a) => a.type);
  if (actionTypes.includes('notify')) return Bell;
  if (actionTypes.includes('assign')) return User;
  if (actionTypes.includes('label')) return Tag;
  if (actionTypes.includes('comment')) return MessageSquare;
  if (actionTypes.includes('move_status')) return ArrowRight;
  if (actionTypes.includes('add_to_sprint')) return LayoutGrid;
  if (actionTypes.includes('create_subtask')) return ListPlus;
  if (actionTypes.includes('ask_ai')) return Bot;
  if (actionTypes.includes('webhook')) return Webhook;
  // Trigger-based fallback
  if (r.trigger.type === 'task.due') return Calendar;
  return Zap;
}

export function RecipeGallery({
  onPick,
  onBack,
}: {
  onPick: (r: AutomationRecipe) => void;
  onBack: () => void;
}) {
  const { data: recipes = [], isLoading } = useAutomationRecipes();
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));
  // The AI-triage recipe's only action is `ask_ai`. Picking it while the
  // assistant is off would build a rule that fires and does nothing.
  const visibleRecipes = isAssistantEnabled
    ? recipes
    : recipes.filter((r) => !r.actions.some((a) => a.type === 'ask_ai'));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Recipe gallery</h3>
        <Button size="sm" variant="ghost" onClick={onBack}>
          Back
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        {visibleRecipes.map((r) => {
          const Icon = recipeIcon(r);
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onPick(r)}
              className="group flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:border-hygge/30 hover:bg-hygge/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge transition-colors group-hover:bg-hygge/15">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{r.name}</div>
                <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                  {r.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
